import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Loader from '../components/Loader.jsx';
import { api } from '../api/client.js';

// /billing/return — where UPayments sends the client back after the hosted
// payment page. The redirect is only a HINT (licensing brief §8.2): this
// page polls the order, and the order endpoint runs the real verification
// (status API + field match) server-side. A lost webhook still fulfils
// here; a forged redirect fulfils nothing.

const POLL_MS = 3_000;
const GIVE_UP_MS = 90_000;

export default function BillingReturn() {
  const [params] = useSearchParams();
  const orderId = params.get('order');
  const pkg = params.get('pkg') || '';
  // Presence check, not value check: UPayments appends its result params to
  // our cancelUrl with a second '?' (observed live), so the value reads as
  // "1?payment_id=…" — the param existing at all is the cancel signal.
  const cancelled = params.get('cancelled') != null;
  // The same '?'-append buries track_id inside another param's value, so
  // regex the raw URL rather than trusting URLSearchParams. The track id is
  // the verification key: the charge response doesn't return one, so this
  // redirect is where we first learn it — passed to the order endpoint as
  // an untrusted hint (the server only believes the status API).
  const trackMatch = window.location.href.match(/[?&]track_id=([^&#]+)/);
  const trackId = trackMatch ? decodeURIComponent(trackMatch[1]) : null;
  const [order, setOrder] = useState(null);
  const [gaveUp, setGaveUp] = useState(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!orderId || cancelled) return undefined;
    let alive = true;
    let timer = null;
    async function poll() {
      try {
        const o = await api.orderStatus(orderId, trackId);
        if (!alive) return;
        setOrder(o);
        if (o.status === 'fulfilled' || o.status === 'failed') return;
      } catch { /* keep polling */ }
      if (!alive) return;
      if (Date.now() - startedAt.current > GIVE_UP_MS) { setGaveUp(true); return; }
      timer = setTimeout(poll, POLL_MS);
    }
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [orderId, cancelled]);

  const backLink = pkg ? `/${pkg}` : '/';
  const back = (
    <Link to={backLink} className="btn btn-outline-primary btn-sm mt-3">
      ← Back to the console
    </Link>
  );

  let content;
  if (!orderId) {
    content = <p className="text-muted">Missing order reference.</p>;
  } else if (cancelled) {
    content = (
      <>
        <h5 className="mb-2">Payment cancelled</h5>
        <p className="text-muted mb-0">Nothing was charged. Your balance is unchanged.</p>
      </>
    );
  } else if (order?.status === 'fulfilled') {
    content = (
      <>
        <h5 className="mb-2">Payment received ✓</h5>
        <p className="mb-0">
          {order.quantity} run{order.quantity === 1 ? '' : 's'} added — your balance
          is now <strong>{order.balance}</strong>.
        </p>
      </>
    );
  } else if (order?.status === 'failed') {
    content = (
      <>
        <h5 className="mb-2">Payment didn&apos;t complete</h5>
        <p className="text-muted mb-0">
          Nothing was credited. If you were charged, contact your admin — the
          order reference is <code>{orderId}</code>.
        </p>
      </>
    );
  } else if (gaveUp) {
    content = (
      <>
        <h5 className="mb-2">Still confirming…</h5>
        <p className="text-muted mb-0">
          The payment is taking longer than usual to confirm. Your runs will
          appear automatically once it clears — no need to pay again. Order
          reference: <code>{orderId}</code>.
        </p>
      </>
    );
  } else {
    content = <Loader label="Confirming your payment…" />;
  }

  return (
    <div className="row justify-content-center mt-5">
      <div className="col-md-6 text-center">
        {content}
        {back}
      </div>
    </div>
  );
}
